// ChangePassword — rotate your own password.
//
// Serves two cases with one form:
//   * FORCED. A seeded or newly-created account carries only the `pwreset`
//     group, so the server refuses every route except this one. Landing the user
//     here is what turns a wall of 403s into an obvious next step.
//   * VOLUNTARY. Reached from the account menu at any time.
//
// After a successful rotation the EXISTING token is promoted server-side (role
// is resolved from the store on every request), so we refresh the identity and
// continue rather than forcing a re-login.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/Button";
import { useAuth } from "@/lib/auth/AuthProvider";

// Mirrors auth.MinPasswordLen on the server. Checked there too — this is a
// courtesy so the user learns before a round-trip, not a control.
const MIN_LEN = 12;

export default function ChangePassword() {
  const { token, identity, mustChangePassword, refreshIdentity } = useAuth();
  const navigate = useNavigate();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const tooShort = next.length > 0 && next.length < MIN_LEN;
  const mismatch = confirm.length > 0 && next !== confirm;
  const sameAsCurrent = next.length > 0 && next === current;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (next !== confirm) {
      setErr("The new passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/v1/password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token ?? ""}`,
        },
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      if (!res.ok) {
        let detail = `Could not change the password (${res.status}).`;
        if (res.status === 401) {
          detail = "Your current password is not correct.";
        } else {
          try {
            const body = await res.json();
            if (typeof body.detail === "string") detail = body.detail;
          } catch {
            // not JSON; keep the status message
          }
        }
        throw new Error(detail);
      }
      setDone(true);
      // Clears `pwreset` and restores the real role on the existing token.
      await refreshIdentity();
      navigate("/overview", { replace: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full items-center justify-center bg-surface p-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-surface-elev p-6"
      >
        <div>
          <img src="/bluerock-logo.svg" alt="BlueRock" className="h-7 w-auto" />
          <div className="mt-1 text-xs text-text-muted">
            {mustChangePassword ? "Choose a new password" : "Change your password"}
          </div>
        </div>

        {mustChangePassword && (
          <div className="rounded border border-warning/40 bg-warning/10 p-2 text-xs text-warning">
            This account still uses the password it was created with. Choose a new
            one to continue — the rest of the console is unavailable until you do.
          </div>
        )}

        {identity?.subject && (
          <div className="text-xs text-text-muted">
            Signed in as <span className="font-medium">{identity.subject}</span>
          </div>
        )}

        <label className="block">
          <span className="mb-1 block text-sm font-medium">Current password</span>
          <input
            autoFocus
            required
            type="password"
            autoComplete="current-password"
            className="w-full rounded border border-border bg-surface px-3 py-2 text-sm"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium">New password</span>
          <input
            required
            type="password"
            autoComplete="new-password"
            className="w-full rounded border border-border bg-surface px-3 py-2 text-sm"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
          <span className="mt-1 block text-xs text-text-muted">
            At least {MIN_LEN} characters.
          </span>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium">Confirm new password</span>
          <input
            required
            type="password"
            autoComplete="new-password"
            className="w-full rounded border border-border bg-surface px-3 py-2 text-sm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </label>

        {/* Local hints, shown before a round-trip. The server rejects all three
            independently. */}
        {tooShort && (
          <div className="text-xs text-text-muted">
            That is {next.length} characters; {MIN_LEN} are required.
          </div>
        )}
        {mismatch && (
          <div className="text-xs text-danger">The new passwords do not match.</div>
        )}
        {sameAsCurrent && (
          <div className="text-xs text-danger">
            The new password must differ from the current one.
          </div>
        )}

        {err && (
          <div className="rounded border border-danger/40 bg-danger/10 p-2 text-xs text-danger">
            {err}
          </div>
        )}

        <Button
          type="submit"
          variant="primary"
          disabled={busy || done || tooShort || mismatch || sameAsCurrent}
          className="w-full"
        >
          {busy ? "Saving…" : done ? "Saved" : "Change password"}
        </Button>
      </form>
    </div>
  );
}
